import type {
  AnalyticsConversationsView,
  AnalyticsOverviewView,
  HomeKpiView,
  AnalyticsRangeQuery,
  CsatSummaryView,
  DeliveryReportView,
  EntryPointReportView,
  CommerceFunnelView,
  GmvTrendPointView,
  LeaderboardView,
  TemplatePerformanceRowView,
  PaymentFunnelView,
} from "../analytics-types";
import type { OverviewResponse } from "../overview-types";
import type { OtpStatsView } from "../types";
import type { AutomationFunnel } from "../automation-admin-types";
import type { SlaReportRow } from "../routing-types";
import type { ApiClient } from "../api-client";
import type { HttpTransport } from "../http/transport";

function analyticsQs(q: AnalyticsRangeQuery): string {
  const p = new URLSearchParams({ from: q.from, to: q.to });
  if (q.channelId) p.set("channelId", q.channelId);
  return `?${p.toString()}`;
}

export type AnalyticsApiMethods = Pick<
  ApiClient,
  | "getCommerceFunnel"
  | "getGmvTrend"
  | "getTemplatePerformance"
  | "getLeaderboard"
  | "setLeaderboardEnabled"
  | "getAnalyticsOverview"
  | "getHomeKpis"
  | "getOverview"
  | "getEntryPoints"
  | "getDeliveryReport"
  | "exportDeliveryReport"
  | "exportEntryPoints"
  | "getPaymentFunnel"
  | "getAnalyticsConversations"
  | "getCsatSummary"
  | "getSlaReport"
  | "getAutomationFunnel"
  | "getOtpStats"
>;

export function createAnalyticsApi({
  request,
  requestCsv,
  ws,
}: HttpTransport): AnalyticsApiMethods {
  return {
    getCommerceFunnel: (id, q) =>
      request<CommerceFunnelView>("GET", ws(id) + "/analytics/commerce/funnel" + analyticsQs(q)),
    getGmvTrend: async (id, q) =>
      (
        await request<{ points: GmvTrendPointView[] }>(
          "GET",
          ws(id) + "/analytics/commerce/gmv-trend" + analyticsQs(q),
        )
      ).points,
    getTemplatePerformance: async (id, q) =>
      (
        await request<{ templates: TemplatePerformanceRowView[] }>(
          "GET",
          ws(id) +
            "/analytics/templates/performance" +
            analyticsQs(q) +
            (q.category ? `&category=${encodeURIComponent(q.category)}` : ""),
        )
      ).templates,
    getLeaderboard: async (id, q) => {
      try {
        return await request<LeaderboardView>(
          "GET",
          ws(id) + "/analytics/leaderboard" + analyticsQs(q),
        );
      } catch (error) {
        if ((error as { code?: string }).code === "LEADERBOARD_DISABLED") return null;
        throw error;
      }
    },
    setLeaderboardEnabled: async (id, enabled) => {
      await request("PUT", ws(id) + "/analytics/leaderboard/settings", { enabled });
    },
    getHomeKpis: async (id) =>
      (await request<{ kpis: HomeKpiView[] }>("GET", ws(id) + "/home/kpis")).kpis,
    getOverview: (id, period) =>
      request<OverviewResponse>("GET", `${ws(id)}/overview?period=${encodeURIComponent(period)}`),
    getAnalyticsOverview: (id, q) =>
      request<AnalyticsOverviewView>("GET", ws(id) + "/analytics/overview" + analyticsQs(q)),
    getEntryPoints: (id, q, groupBy) =>
      request<EntryPointReportView>(
        "GET",
        ws(id) + "/analytics/entry-points" + analyticsQs(q) + "&groupBy=" + groupBy,
      ),
    exportEntryPoints: (id, q, groupBy) =>
      requestCsv(
        "GET",
        ws(id) + "/analytics/entry-points" + analyticsQs(q) + "&groupBy=" + groupBy + "&format=csv",
      ),
    getDeliveryReport: (id, q) =>
      request<DeliveryReportView>("GET", ws(id) + "/analytics/delivery" + analyticsQs(q)),
    exportDeliveryReport: (id, q) =>
      requestCsv("GET", ws(id) + "/analytics/delivery" + analyticsQs(q) + "&format=csv"),
    getPaymentFunnel: (id, q) =>
      request<PaymentFunnelView>("GET", ws(id) + "/analytics/payments/funnel" + analyticsQs(q)),
    getAnalyticsConversations: (id, q) =>
      request<AnalyticsConversationsView>(
        "GET",
        ws(id) + "/analytics/conversations" + analyticsQs(q),
      ),
    getCsatSummary(id, q) {
      return request<CsatSummaryView>("GET", ws(id) + "/analytics/csat" + analyticsQs(q));
    },
    async getSlaReport(id, from, to) {
      const q = new URLSearchParams();
      if (from) q.set("from", from);
      if (to) q.set("to", to);
      const qs = q.toString();
      return (
        await request<{ rows: SlaReportRow[] }>("GET", `${ws(id)}/sla-report${qs ? `?${qs}` : ""}`)
      ).rows;
    },
    async getAutomationFunnel(id) {
      return (await request<{ funnel: AutomationFunnel }>("GET", `${ws(id)}/automation-analytics`))
        .funnel;
    },
    getOtpStats: (id, days = 30) =>
      request<OtpStatsView>("GET", `${ws(id)}/otp/stats?days=${days}`),
  };
}
